import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Building2, Plus, Trash2, ExternalLink, Edit2, Save,
  X, Image as ImageIcon, GripVertical, RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const fileInputRef = useRef(null);

  const [newSponsor, setNewSponsor] = useState({
    name: '',
    website: '',
    description: '',
    order: 0,
  });
  const [newLogo, setNewLogo] = useState(null);

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const res = await axios.get('/api/sponsors/all');
      setSponsors(res.data);
    } catch (err) {
      toast.error('Failed to load sponsors');
    } finally {
      setLoading(false);
    }
  };

  const addSponsor = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', newSponsor.name);
      formData.append('website', newSponsor.website);
      formData.append('description', newSponsor.description);
      formData.append('order', sponsors.length + 1);
      if (newLogo) {
        formData.append('logo', newLogo);
      }

      await axios.post('/api/sponsors', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Sponsor added!');
      setNewSponsor({ name: '', website: '', description: '', order: 0 });
      setNewLogo(null);
      setShowAdd(false);
      fetchSponsors();
    } catch (err) {
      toast.error('Failed to add sponsor');
    }
  };

  const updateSponsor = async (id) => {
    try {
      const formData = new FormData();
      if (editForm.name) formData.append('name', editForm.name);
      if (editForm.website) formData.append('website', editForm.website);
      if (editForm.description) formData.append('description', editForm.description);

      await axios.put(`/api/sponsors/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Sponsor updated!');
      setEditingId(null);
      setEditForm({});
      fetchSponsors();
    } catch (err) {
      toast.error('Failed to update sponsor');
    }
  };

  const deleteSponsor = async () => {
    if (!deleteId) return;
    const idToDelete = deleteId;
    setDeleteId(null);
    try {
      await axios.delete(`/api/sponsors/${idToDelete}`);
      toast.success('Sponsor deleted');
      fetchSponsors();
    } catch (err) {
      toast.error('Failed to delete sponsor');
    }
  };

  const startEditing = (sponsor) => {
    setEditingId(sponsor.id);
    setEditForm({
      name: sponsor.name,
      website: sponsor.website,
      description: sponsor.description,
    });
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-4">
        <Skeleton className="h-8 w-48" />
        {[1,2,3].map(i => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-5xl mx-auto space-y-6 animate-fadeInUp">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
            Recommended Sponsors
          </h1>
          <p className="text-sm text-muted-foreground">{sponsors.length} sponsor{sponsors.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchSponsors} className="gap-2">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Sponsor
          </Button>
        </div>
      </div>

      {/* Add Sponsor Form */}
      {showAdd && (
        <Card className="bg-card/80 border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="w-4 h-4 text-primary" />
              Add New Sponsor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={addSponsor} className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Company Name *</Label>
                <Input
                  value={newSponsor.name}
                  onChange={(e) => setNewSponsor({ ...newSponsor, name: e.target.value })}
                  placeholder="e.g. 1xBet"
                  className="bg-muted/30 border-border/70"
                  required
                  data-testid="sponsor-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input
                  value={newSponsor.website}
                  onChange={(e) => setNewSponsor({ ...newSponsor, website: e.target.value })}
                  placeholder="https://example.com"
                  className="bg-muted/30 border-border/70"
                  data-testid="sponsor-website-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={newSponsor.description}
                  onChange={(e) => setNewSponsor({ ...newSponsor, description: e.target.value })}
                  placeholder="Short description..."
                  className="bg-muted/30 border-border/70"
                  data-testid="sponsor-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Logo</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setNewLogo(e.target.files[0])}
                  className="bg-muted/30 border-border/70"
                  data-testid="sponsor-logo-input"
                />
              </div>
              <div className="sm:col-span-2 flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button type="submit" className="gap-2" data-testid="sponsor-submit-button">
                  <Save className="w-4 h-4" /> Save Sponsor
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Sponsors List */}
      <div data-testid="sponsors-list" className="space-y-3">
        {sponsors.length === 0 ? (
          <Card className="bg-card/80 border-border/60">
            <CardContent className="py-12 text-center">
              <Building2 className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No sponsors yet</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAdd(true)}>
                Add First Sponsor
              </Button>
            </CardContent>
          </Card>
        ) : (
          sponsors.map((sponsor) => (
            <Card
              key={sponsor.id}
              className={`bg-card/80 border-border/60 hover:border-border transition-colors duration-150 ${
                !sponsor.is_active ? 'opacity-50' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  {/* Logo */}
                  {sponsor.logo_url ? (
                    <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border border-border/40">
                      <img
                        src={`${BACKEND_URL}${sponsor.logo_url}`}
                        alt={sponsor.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Space Grotesk' }}>
                        {sponsor.name.charAt(0)}
                      </span>
                    </div>
                  )}

                  {/* Info */}
                  {editingId === sponsor.id ? (
                    <div className="flex-1 grid sm:grid-cols-3 gap-2">
                      <Input
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="bg-muted/30 border-border/70 text-sm"
                        placeholder="Name"
                      />
                      <Input
                        value={editForm.website}
                        onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                        className="bg-muted/30 border-border/70 text-sm"
                        placeholder="Website"
                      />
                      <Input
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="bg-muted/30 border-border/70 text-sm"
                        placeholder="Description"
                      />
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold">{sponsor.name}</h3>
                        {!sponsor.is_active && (
                          <span className="badge-inactive px-2 py-0.5 rounded-full text-[10px] font-medium">Inactive</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{sponsor.description}</p>
                      {sponsor.website && (
                        <a
                          href={sponsor.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1 mt-0.5"
                        >
                          {sponsor.website}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {editingId === sponsor.id ? (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => updateSponsor(sponsor.id)}
                        >
                          <Save className="w-3.5 h-3.5 text-primary" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => { setEditingId(null); setEditForm({}); }}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => startEditing(sponsor)}
                          title="Edit"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => setDeleteId(sponsor.id)}
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sponsor</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this sponsor?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteSponsor}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
